import { useCallback, useEffect, useRef, useState } from 'react';
import type { BadgeTone } from '@/components/ui/Badge';
import { useTranslation } from '@/i18n';
import type { Key } from '@/i18n';
import { ApiError } from '@/lib/api/types';
import { cx } from '@/lib/cx';
import type { FieldChange, Health } from './api';
import { fieldLabel, formatValue, isEmpty } from './changes';
import styles from './MonitorPage.module.css';

/** The documented colours: green, amber, red — and grey for "no data yet". */
export const healthTone: Record<Health, BadgeTone> = {
  OK: 'success',
  STALE: 'warning',
  FAILING: 'danger',
  UNKNOWN: 'neutral',
};

/**
 * The same changes on one line, for the table cell.
 *
 * Elements rather than the joined string this used to be, so a real diff is
 * distinguishable at a glance from the rows that have none: a label is muted, a new
 * value is not, and the eye can tell "three fields moved" from "an exception
 * happened" without reading either. It stays on one line and truncates, because a
 * table whose rows are different heights cannot be scanned.
 */
export const ChangeSummary = ({ changes }: { changes: readonly FieldChange[] }) => {
  const { t } = useTranslation();

  return (
    <>
      {changes.map((change, index) => {
        const cleared = isEmpty(change.newValue);

        return (
          <span className={styles.summaryItem} key={`${change.field}-${index}`}>
            <span className={styles.summaryField}>{fieldLabel(change.field, t)}</span>{' '}
            <span className={cleared ? styles.changeCleared : styles.summaryValue}>
              {cleared
                ? t('monitor.changes.cleared')
                : formatValue(change.field, change.newValue ?? '', t)}
            </span>
          </span>
        );
      })}
    </>
  );
};

/**
 * Every change on an event, field by field — the whole of an expanded row.
 *
 * What each part MEANS is decided in `changes.ts`; this only lays it out, in the
 * four aligned columns the stylesheet defines.
 *
 * **A creation drops to two columns.** Its changes are all `null -> value`, so a
 * "was" column would be empty on every line and the arrow would point out of
 * nothing — a whole column of punctuation saying "this is new", which the absence of
 * a previous value already says. A mixed list keeps four and marks the odd line out
 * with a dash, because there alignment is what makes the two kinds distinguishable.
 */
export const ChangeList = ({ changes }: { changes: readonly FieldChange[] }) => {
  const { t } = useTranslation();

  const anyPrevious = changes.some((change) => !isEmpty(change.oldValue));

  return (
    <ul className={cx(styles.changes, !anyPrevious && styles.changesSet)}>
      {changes.map((change, index) => {
        const cleared = isEmpty(change.newValue);

        return (
          <li className={styles.change} key={`${change.field}-${index}`}>
            <span className={styles.changeField}>{fieldLabel(change.field, t)}</span>

            {anyPrevious && (
              <>
                <span className={styles.changeOld}>
                  {isEmpty(change.oldValue)
                    ? '—'
                    : formatValue(change.field, change.oldValue ?? '', t)}
                </span>
                <span className={styles.changeArrow} aria-hidden="true">
                  →
                </span>
              </>
            )}

            <span className={cleared ? styles.changeCleared : styles.changeNew}>
              {cleared
                ? t('monitor.changes.cleared')
                : formatValue(change.field, change.newValue ?? '', t)}
            </span>
          </li>
        );
      })}
    </ul>
  );
};

/** No `idle`: it loads as soon as it is mounted, so it is never waiting to start. */
type PayloadState =
  | { kind: 'loading' }
  | { kind: 'done'; text: string | null }
  | { kind: 'gone' }
  | { kind: 'failed' };

/**
 * What a source sent, fetched when a row is opened.
 *
 * The lists omit `rawData` / `rawPayload` by design — a page of fifty at up to
 * 100 000 characters is megabytes of JSON — so the body lives only at the by-id
 * endpoints. **A null body in a list therefore says nothing about whether the record
 * exists**, which is why the caller gates this component on `hasRawData` /
 * `hasRawPayload` and never on the field itself.
 *
 * Because of that gate, the states left here are narrow: a 404 is the row removed by
 * retention, an empty body is a race with the 14-day clearing, and anything else is
 * worth retrying. None of them guesses at a reason — this used to announce "cleared
 * after 14 days" for every empty answer, which made a source that simply keeps no
 * per-flight record look like it had lost one.
 */
export const LazyPayload = ({
  load,
  emptyKey,
}: {
  load: () => Promise<string | null>;
  /**
   * What an empty body means HERE, which differs by caller and is never guessed.
   * For an event gated on `hasRawData` it can only be a race with the clearing; for
   * a delivery, which cannot be gated before it is fetched, it is the ordinary case
   * of a snapshot that keeps no file.
   */
  emptyKey: Key;
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState<PayloadState>({ kind: 'loading' });

  const open = useCallback(async () => {
    setState({ kind: 'loading' });

    try {
      setState({ kind: 'done', text: await load() });
    } catch (caught) {
      setState(
        caught instanceof ApiError && caught.isNotFound ? { kind: 'gone' } : { kind: 'failed' },
      );
    }
  }, [load]);

  /**
   * Fetched as soon as this is on screen.
   *
   * It is only ever mounted inside an expanded row, so "on mount" means "somebody
   * opened this row to look at it" — and one payload for one row is not what the
   * list omits the column for. It used to be a button, which made seeing the data
   * two clicks and a decision.
   *
   * The ref, not the dependency list, is what stops it repeating. `load` is an
   * inline closure in the caller and so is a new function every render, which makes
   * `open` new too and re-runs this effect; the guard is what turns that into one
   * fetch. Listing the real dependency and guarding the body beats an empty list
   * that lies about what the effect reads.
   */
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void open();
  }, [open]);

  if (state.kind === 'loading') {
    return <p className={styles.payloadNote}>{t('common.loading')}</p>;
  }

  if (state.kind === 'gone') return <p className={styles.payloadNote}>{t('monitor.payloadGone')}</p>;

  if (state.kind === 'failed') {
    return (
      <p className={styles.payloadNote}>
        {t('monitor.payloadFailed')}{' '}
        <button type="button" className={styles.retry} onClick={() => void open()}>
          {t('common.refresh')}
        </button>
      </p>
    );
  }

  if (state.text === null || state.text === '') {
    return <p className={styles.payloadNote}>{t(emptyKey)}</p>;
  }

  return <pre className={styles.payload}>{state.text}</pre>;
};
